import { useId, useState } from "react";
import { useNavigate } from "react-router-dom";
import { trpc } from "../../lib/trpc.js";
import { baixar } from "../../lib/api.js";
import { Botao, Chave, Falha, Selo } from "../../components/ui.js";
import {
  podePedirOEnvio,
  rotuloDoEnvio,
  seloDoEnvio,
  type EnvioNaTela,
} from "./estado-do-envio.js";

/**
 * A entrega.
 *
 * Finalizar publica a peça: ela passa a existir em `generations/final` e fica disponível para imprimir e
 * enviar — e, se a marca estiver ligada, na galeria do salão (#96). É a ação irreversível do fluxo; o botão
 * dela (e, depois, "Atender a próxima pessoa") fica na faixa de ações do painel, ao alcance do polegar. A foto
 * salva sem IA (#95) chega aqui já publicada.
 *
 * O WhatsApp sai pela fila do número da SER (#105): o botão responde na hora com "na fila", e a tela acompanha até
 * "enviada" ou "não enviada" e o motivo — o operador não espera o WhatsApp com a pessoa na frente dele.
 */
export function EtapaEntrega({
  geracaoId,
  finalizada,
  imagemUrl,
  nomeArquivo,
  temParticipante,
  envio,
  naGaleria,
  naGaleriaAoPublicar,
  aoMudarNaGaleriaAoPublicar,
  aoEnviar,
}: {
  geracaoId: number;
  finalizada: boolean;
  imagemUrl?: string | null;
  nomeArquivo?: string | null;
  /** O WhatsApp vai para a pessoa cadastrada; sem ela, não há para onde. */
  temParticipante: boolean;
  /** O último pedido de envio pelo WhatsApp. */
  envio: EnvioNaTela;
  /** A marca "Mostrar na galeria do salão" da foto, depois de publicada. */
  naGaleria: boolean;
  /** A escolha antes de publicar; o botão de finalizar, no rodapé, a leva. */
  naGaleriaAoPublicar: boolean;
  aoMudarNaGaleriaAoPublicar: (v: boolean) => void;
  /** Avisa a página que a foto foi para a fila: o botão principal do rodapé passa a ser o próximo atendimento. */
  aoEnviar?: () => void;
}) {
  const navegar = useNavigate();
  const idDaDica = useId();
  const utils = trpc.useUtils();
  const [aviso, setAviso] = useState<string | null>(null);
  // O WhatsApp vai para a pessoa cadastrada: sem ela, o servidor recusaria.
  const semDestino = !temParticipante;
  const selo = seloDoEnvio(envio);

  const enviarWhatsapp = trpc.whatsapp.sendGeneration.useMutation({
    onSuccess: () => {
      setAviso(null);
      aoEnviar?.();
      // A geração passa a dizer "na fila"; o socket e a reconsulta seguem até o fim.
      void utils.generations.getById.invalidate({ id: geracaoId });
    },
    onError: (e) => setAviso(e.message),
  });

  if (!finalizada) {
    return (
      <div className="flex flex-col gap-4">
        {imagemUrl && (
          <div className="overflow-hidden rounded-peca border border-borda bg-black">
            <img
              src={imagemUrl}
              alt="Peça pronta para finalizar"
              className="max-h-[42dvh] w-full object-contain"
            />
          </div>
        )}

        <p className="text-[0.9375rem] leading-relaxed text-texto-suave">
          Finalizar publica esta foto e libera a impressão e o envio. Depois disso ela não muda
          mais.
        </p>

        <div className="flex flex-col gap-1">
          <Chave
            ligada={naGaleriaAoPublicar}
            aoAlternar={aoMudarNaGaleriaAoPublicar}
            rotulo="Mostrar na galeria do salão"
            texto="Mostrar na galeria do salão"
            descricaoId={idDaDica}
          />
          <p id={idDaDica} className="text-[0.8125rem] text-texto-fraco">
            Dá para mudar depois, na Biblioteca.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {imagemUrl && (
        <div className="overflow-hidden rounded-peca border border-fixado/40 bg-black">
          <img
            src={imagemUrl}
            alt="Peça publicada"
            className="max-h-[42dvh] w-full object-contain"
          />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {naGaleria ? (
          <Selo tom="fixado">Na galeria</Selo>
        ) : (
          <Selo tom="quieto">Fora da galeria</Selo>
        )}
        {selo && <Selo tom={selo.tom}>{selo.texto}</Selo>}
        {nomeArquivo && (
          <span className="font-mono text-[0.75rem] text-texto-fraco">{nomeArquivo}</span>
        )}
      </div>

      {aviso && <Falha mensagem={aviso} />}
      {/* Na fila com motivo é uma nova tentativa a caminho; não enviada, o que impediu. */}
      {envio.motivo && envio.estado === "nao_enviada" && <Falha mensagem={envio.motivo} />}
      {envio.motivo && envio.estado === "na_fila" && (
        <p className="text-[0.8125rem] text-texto-suave">{envio.motivo}</p>
      )}

      <div className="flex flex-col gap-3">
        <Botao
          tom={envio.estado !== null || envio.enviadaEm || semDestino ? "quieto" : "brasa"}
          largo
          disabled={semDestino || !podePedirOEnvio(envio)}
          carregando={enviarWhatsapp.isPending}
          onClick={() => enviarWhatsapp.mutate({ generationId: geracaoId })}
        >
          {rotuloDoEnvio(envio)}
        </Botao>

        {semDestino && (
          <p className="text-[0.8125rem] text-texto-fraco">
            Sem pessoa cadastrada, não há para onde enviar: o WhatsApp vai para a pessoa do
            atendimento.
          </p>
        )}

        {/* A Impressão abre com esta foto já na folha: o mesmo caminho do "Imprimir N" da Biblioteca. */}
        <Botao largo onClick={() => navegar(`/impressao?fotos=${geracaoId}`)}>
          Imprimir
        </Botao>

        <Botao largo onClick={() => baixar(`/api/generations/export?ids=${geracaoId}`)}>
          Baixar arquivo
        </Botao>
      </div>
    </div>
  );
}
